import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { shareUrl } from "@/lib/share";
import VideoEditor from "./VideoEditor";
import TmdbLookupModal from "@/components/TmdbLookupModal";
import BulkTmdbMatchModal from "@/components/BulkTmdbMatchModal";

export default function PlaylistPanel({
  apiBase,
  authHeaders,
  channels,
  selectedChannel,
  channelVideos,
  busy,
  setBusy,
  append,
  onUploaded,
}) {
  // Sub-channels are frontend-derived groupings — their id doesn't map
  // to a DB doc, so channel-level API calls (reorder, sort, scan, audit,
  // purge, delete-all) 404 when targeted at the sub id. We transparently
  // proxy those to the parent channel's real id; per-tape ops (video PATCH,
  // /videos/{id}) work regardless of which sub the row is displayed under.
  const selectedCh = (channels || []).find((c) => c.id === selectedChannel);
  const isSubChannel = !!selectedCh?.isSubChannel;
  const effectiveChannelId = isSubChannel ? selectedCh.parent_id : selectedChannel;
  const [editingVideo, setEditingVideo] = useState(null);
  const [tmdbTarget, setTmdbTarget] = useState(null); // video row currently in the TMDb picker
  const [bulkTmdbOpen, setBulkTmdbOpen] = useState(false);
  // Compact mode caps the playlist at ~5 rows for the upload terminal sidebar.
  // Owners with long playlists want all titles visible at once when bulk-
  // editing — flipping to expanded mode swaps to a tall scroller and surfaces
  // every row without forcing a tiny inner-scroll workflow.
  // Phase 2.3.21 — Playlist is now a full-height VCR workspace rather than
  // a compact sidebar widget. `expanded` now controls row detail density.
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState("ALL");

  // Bulk title-edit mode: every row swaps to a title-only input. Drafts live
  // in a `videoId -> draftTitle` map so background list refreshes (from
  // onUploaded after other actions) don't clobber in-progress edits.
  const [bulkMode, setBulkMode] = useState(false);
  const [draftTitles, setDraftTitles] = useState({});

  // Move mode — checkbox-select tapes and move them to another channel in
  // one click. Separate from bulkMode (title editing) so they can't fight
  // for the same row's UX. moveSelection holds the picked video IDs; the
  // destination channel is picked in the floating action bar at the
  // panel header.
  const [moveMode, setMoveMode] = useState(false);
  const [moveSelection, setMoveSelection] = useState(() => new Set());
  const [moveTarget, setMoveTarget] = useState("");

  // Tag-show mode — sibling to move mode. Multi-select tapes, type a
  // show title, PATCH them all in one shot. Two-or-more distinct
  // `show_title` values on a channel is what spawns Sky-Q sub-channels
  // (via `expandChannelsWithShows`), so this is the fastest way to
  // retro-fit existing mixed-content channels into sub-channels
  // without renaming files or round-tripping through TMDb.
  // Reuses `moveSelection` because the two modes are mutually
  // exclusive — one selection set keeps the row UX consistent.
  const [tagShowMode, setTagShowMode] = useState(false);
  const [tagShowDraft, setTagShowDraft] = useState("");
  // Inline "+ NEW CHANNEL" shortcut for move mode — creates a fresh
  // channel and moves the selected tapes into it in one click. Avoids
  // the round-trip through Channel Management when the owner realises
  // mid-reorganisation that they want a new bucket (e.g. SCI-FI).
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const channelsForMove = React.useMemo(() => {
    // Only real channels are valid destinations — virtual rows from
    // ChannelGuide (★ CONTACT / FAVOURITES / CONTINUE WATCHING) have no
    // backend channel doc and would 400 the move endpoint. They're
    // filtered out by the `id`-must-be-a-string check below.
    return (channels || []).filter((c) => c && typeof c.id === "string" && c.id);
  }, [channels]);

  const blockedCount = channelVideos.filter((v) => v.embed_blocked).length;
  const ytCount = channelVideos.filter((v) => v.kind === "youtube").length;
  // Legacy uploads (predate the content-hash field) still need fingerprinting
  // so rename-survival dedup works on the existing library. Surfaced via an
  // owner-only AUDIT button that streams the first 1 MB per tape from
  // object storage and writes the SHA-256 back to the doc.
  const legacyUnhashedCount = channelVideos.filter(
    (v) => v.kind === "upload" && !v.content_hash
  ).length;
  const [scanning, setScanning] = useState(false);
  const [auditing, setAuditing] = useState(false);
  // Track per-video re-analyse state so the button stays disabled +
  // reads "ANALYSING…" while the request is in-flight. Keyed by video.id
  // because multiple buttons (one per row) can fire independently.
  const [reanalysing, setReanalysing] = useState({});

  // Format the per-tape LUFS reading into a compact pill: a precise
  // number ("-18 LUFS") when measured, "N/A" when the analyser ran but
  // couldn't get a usable reading (silent / un-decodable), or "PENDING"
  // when we haven't tried yet. Returns null for tapes the pill doesn't
  // apply to (YouTube / archive embeds — we don't control their audio).
  const lufsBadge = (v) => {
    if (v.kind !== "upload") return null;
    if (typeof v.lufs === "number") {
      return { text: `${v.lufs.toFixed(1)} LUFS`, tone: "ok", title: "Loudness measured — gain will be applied at playback" };
    }
    if (v.lufs_attempted_at) {
      return { text: "LUFS N/A", tone: "warn", title: "Analyser ran but couldn't get a usable reading (silent or un-decodable audio)" };
    }
    return { text: "LUFS PENDING", tone: "pending", title: "Not yet analysed — re-run BACKFILL or use the ↻ button" };
  };

  const reanalyseVideo = async (videoId) => {
    if (reanalysing[videoId]) return;
    // Snapshot the video's current state so the poll loop knows when
    // the backend background worker has changed something.
    const before = channelVideos.find((v) => v.id === videoId) || {};
    const beforeLufs = typeof before.lufs === "number" ? before.lufs : null;
    const beforeAttempted = before.lufs_attempted_at || null;
    setReanalysing((m) => ({ ...m, [videoId]: true }));
    try {
      await axios.post(
        `${apiBase}/owner/videos/${videoId}/reanalyse-loudness`,
        null,
        { headers: { ...authHeaders() } },
      );
      append(`> LOUDNESS: ${videoId.slice(0, 8)}… queued — analysing in background`);

      // Background analysis can take 30s-several minutes per tape, well
      // past Cloudflare's 100s origin-timeout limit. The backend now
      // returns 202 immediately and we poll the videos list every 4s
      // for up to 5 min, looking for this video's lufs or
      // lufs_attempted_at field to change.
      const POLL_INTERVAL_MS = 4000;
      const POLL_MAX_MS = 5 * 60 * 1000;
      const started = Date.now();
      let finished = false;
      while (!finished && Date.now() - started < POLL_MAX_MS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (onUploaded) await onUploaded();
        // `channelVideos` is a prop and React batches the re-render
        // after `onUploaded`, so the closure here still sees the stale
        // value. Read the latest by reaching into the channel doc
        // directly via a fresh fetch.
        try {
          const ch = selectedChannel;
          if (!ch) break;
          const { data } = await axios.get(`${apiBase}/channels/${ch.id}/videos`);
          const fresh = (data || []).find((v) => v.id === videoId);
          if (!fresh) { finished = true; break; }
          const lufsChanged = (typeof fresh.lufs === "number") && fresh.lufs !== beforeLufs;
          const attemptedChanged = (fresh.lufs_attempted_at || null) !== beforeAttempted
            && !!fresh.lufs_attempted_at;
          if (lufsChanged) {
            append(`> LOUDNESS: ${videoId.slice(0, 8)}… ${fresh.lufs.toFixed(1)} LUFS`);
            finished = true;
          } else if (attemptedChanged) {
            append(`> LOUDNESS: ${videoId.slice(0, 8)}… UNMEASURABLE (silent / un-decodable)`);
            finished = true;
          }
        } catch (pollErr) { /* keep polling */ }
      }
      if (!finished) {
        append(`> LOUDNESS: ${videoId.slice(0, 8)}… still running after 5 min — check back later`);
      }
    } catch (e) {
      append(`> LOUDNESS: re-analyse failed — ${e.response?.data?.detail || e.message}`);
    } finally {
      setReanalysing((m) => {
        const next = { ...m };
        delete next[videoId];
        return next;
      });
    }
  };

  const startBulk = () => {
    const map = {};
    channelVideos.forEach((v) => { map[v.id] = v.title || ""; });
    setDraftTitles(map);
    setEditingVideo(null);
    setBulkMode(true);
    setExpanded(true);
  };

  const cancelBulk = () => {
    setBulkMode(false);
    setDraftTitles({});
  };

  const saveBulk = async () => {
    const changed = channelVideos.filter((v) => {
      const draft = draftTitles[v.id];
      if (draft == null) return false;
      return draft.trim() !== (v.title || "");
    });
    if (!changed.length) {
      append("> NO TITLE CHANGES TO SAVE.");
      setBulkMode(false);
      setDraftTitles({});
      return;
    }
    setBusy(true);
    append(`> SAVING ${changed.length} TITLE EDIT${changed.length === 1 ? "" : "S"}...`);
    let ok = 0;
    let fail = 0;
    for (const v of changed) {
      try {
        await axios.patch(
          `${apiBase}/videos/${v.id}`,
          { title: draftTitles[v.id].trim() || null },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        );
        ok += 1;
      } catch (e) {
        fail += 1;
        append(`> FAILED — ${v.title} (${e.response?.data?.detail || e.message})`);
      }
    }
    append(`> DONE. ${ok} SAVED${fail ? `, ${fail} FAILED` : ""}.`);
    await onUploaded();
    setBulkMode(false);
    setDraftTitles({});
    setBusy(false);
  };

  const handleScanChannel = async () => {
    if (!selectedChannel || ytCount === 0) return;
    setScanning(true);
    setBusy(true);
    append(`> SCANNING ${ytCount} YOUTUBE TAPE${ytCount === 1 ? "" : "S"} VIA DATA API...`);
    try {
      const r = await axios.post(
        `${apiBase}/channels/${effectiveChannelId}/youtube/scan`,
        {},
        { headers: authHeaders() }
      );
      const { scanned = 0, newly_blocked: nb = 0, newly_cleared: nc = 0, unchanged: u = 0, skipped: sk = 0 } = r.data || {};
      const parts = [`${scanned} CHECKED`];
      if (nb) parts.push(`${nb} NEWLY BLOCKED`);
      if (nc) parts.push(`${nc} UN-BLOCKED`);
      if (u) parts.push(`${u} UNCHANGED`);
      if (sk) parts.push(`${sk} SKIPPED`);
      append(`> DONE. ${parts.join(", ")}.`);
      await onUploaded();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setScanning(false);
      setBusy(false);
    }
  };

  const handlePurgeBlocked = async () => {
    if (!blockedCount) return;
    const confirmed = window.confirm(
      `Delete ${blockedCount} embed-blocked YouTube tape${blockedCount === 1 ? "" : "s"} from this channel?\n\nThese are tapes YouTube refused to embed — they will never play here.`
    );
    if (!confirmed) return;
    setBusy(true);
    append(`> PURGING ${blockedCount} BLOCKED TAPE${blockedCount === 1 ? "" : "S"}...`);
    try {
      const r = await axios.post(
        `${apiBase}/channels/${effectiveChannelId}/videos/purge-blocked`,
        {},
        { headers: authHeaders() }
      );
      append(`> DONE. ${r.data?.purged ?? 0} PURGED.`);
      await onUploaded();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Audit pass: ask the backend to walk every upload-kind tape on the
  // channel that's missing `content_hash`, stream the first 1 MB from
  // object storage and fingerprint it. Lets rename-survival dedup work
  // against pre-existing library tapes that predate the field.
  const handleAuditHashes = async () => {
    if (!legacyUnhashedCount) {
      append("> NOTHING TO AUDIT — every upload-kind tape is already fingerprinted.");
      return;
    }
    const confirmed = window.confirm(
      `Audit ${legacyUnhashedCount} legacy tape${legacyUnhashedCount === 1 ? "" : "s"} for content fingerprints?\n\nStreams the first 1 MB of each from object storage. Sequential, can take a minute on big libraries.`
    );
    if (!confirmed) return;
    setBusy(true);
    setAuditing(true);
    append(`> AUDITING ${legacyUnhashedCount} TAPE${legacyUnhashedCount === 1 ? "" : "S"}... (streaming 1 MB per tape)`);
    try {
      const r = await axios.post(
        `${apiBase}/channels/${effectiveChannelId}/videos/audit-hashes`,
        {},
        { headers: authHeaders(), timeout: 600000 }
      );
      const { hashed = 0, failed = 0, candidates = 0 } = r.data || {};
      append(`> DONE. ${hashed}/${candidates} HASHED${failed ? `, ${failed} FAILED` : ""}.`);
      if (failed && Array.isArray(r.data?.details)) {
        for (const d of r.data.details) {
          if (d.status === "failed") {
            append(`> FAILED — ${d.title || d.id} (${d.error || "unknown"})`);
          }
        }
      }
      await onUploaded();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setAuditing(false);
      setBusy(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    setBusy(true);
    append(`> ERASING TAPE ${videoId.slice(0, 6)}...`);
    try {
      await axios.delete(`${apiBase}/videos/${videoId}`, { headers: authHeaders() });
      append("> ERASED.");
      await onUploaded();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Wipe every tape on the selected channel. Confirmed via native prompt so
  // an accidental click can't nuke a long playlist. Failures are individually
  // reported but never stop the loop — the goal is "best effort, tell me what
  // didn't go".
  const handleDeleteAll = async () => {
    if (!channelVideos.length) return;
    const count = channelVideos.length;
    const confirmed = window.confirm(
      `Delete ALL ${count} tape${count === 1 ? "" : "s"} from this channel?\n\nThis can't be undone.`
    );
    if (!confirmed) return;
    setBusy(true);
    append(`> WIPING PLAYLIST (${count} TAPE${count === 1 ? "" : "S"})...`);
    let ok = 0;
    let fail = 0;
    // Snapshot the list so a mid-loop refetch (from onUploaded) doesn't shift
    // indexes underneath us.
    for (const v of [...channelVideos]) {
      try {
        await axios.delete(`${apiBase}/videos/${v.id}`, { headers: authHeaders() });
        ok += 1;
      } catch (e) {
        fail += 1;
        append(`> FAILED — ${v.title} (${e.response?.data?.detail || e.message})`);
      }
    }
    append(`> DONE. ${ok} ERASED${fail ? `, ${fail} FAILED` : ""}.`);
    await onUploaded();
    setBusy(false);
  };

  // --- Tag-show mode -----------------------------------------------------------
  // Distinct show titles already present on this channel — surfaced as
  // a datalist so tagging the 2nd/3rd episode of the same show is a
  // one-click pick rather than a re-type (and avoids typos that would
  // split a show across two spelling variants).
  const existingShowTitles = React.useMemo(() => {
    const seen = new Set();
    for (const v of channelVideos) {
      const s = (v.show_title || "").trim();
      if (s) seen.add(s);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [channelVideos]);

  const startTagShow = () => {
    setTagShowMode(true);
    setMoveSelection(new Set());
    setTagShowDraft("");
    // Bail out of other modes so the row UI doesn't conflict.
    setMoveMode(false);
    setBulkMode(false);
    setDraftTitles({});
    setEditingVideo(null);
  };
  const cancelTagShow = () => {
    setTagShowMode(false);
    setMoveSelection(new Set());
    setTagShowDraft("");
  };
  const handleBulkTagShow = async () => {
    const raw = (tagShowDraft || "").trim();
    // Empty string clears the show tag (backend `VideoUpdate` treats
    // empty show_title as a clear). We keep the empty-input case
    // as an explicit "UNTAG" pathway so operators can bulk-remove a
    // wrongly-applied show without deleting the tape.
    const showTitle = raw.length ? raw.slice(0, 120) : "";
    const ids = Array.from(moveSelection);
    if (!ids.length) {
      append("> NO TAPES SELECTED — nothing to tag.");
      return;
    }
    setBusy(true);
    const verb = showTitle ? `TAG AS "${showTitle.toUpperCase()}"` : "UNTAG SHOW";
    append(`> ${verb} — ${ids.length} TAPE${ids.length === 1 ? "" : "S"}...`);
    let ok = 0;
    let fail = 0;
    // Parallel PATCHes — same idempotent /videos/{id} endpoint used
    // elsewhere; safe to hit concurrently on distinct ids.
    const results = await Promise.allSettled(
      ids.map((id) =>
        axios.patch(
          `${apiBase}/videos/${id}`,
          { show_title: showTitle },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        )
      )
    );
    for (const r of results) (r.status === "fulfilled" ? ok++ : fail++);
    append(`> DONE. ${ok} TAGGED${fail ? `, ${fail} FAILED` : ""}.`);
    cancelTagShow();
    await onUploaded(effectiveChannelId);
    setBusy(false);
  };

  // --- Move mode -----------------------------------------------------------
  const startMove = () => {
    setMoveMode(true);
    setMoveSelection(new Set());
    setMoveTarget("");
    // Bail out of other modes so the row UI doesn't conflict.
    setTagShowMode(false);
    setTagShowDraft("");
    setBulkMode(false);
    setDraftTitles({});
    setEditingVideo(null);
  };
  const cancelMove = () => {
    setMoveMode(false);
    setMoveSelection(new Set());
    setMoveTarget("");
    setCreatingChannel(false);
    setNewChannelName("");
  };
  const toggleMoveSelect = (videoId) => {
    setMoveSelection((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) next.delete(videoId);
      else next.add(videoId);
      return next;
    });
  };
  const selectAllForMove = () => {
    setMoveSelection(new Set(channelVideos.map((v) => v.id)));
  };
  const handleBulkMove = async () => {
    if (!moveTarget) {
      append("> CHOOSE A DESTINATION CHANNEL FIRST.");
      return;
    }
    const ids = Array.from(moveSelection);
    if (!ids.length) {
      append("> NO TAPES SELECTED.");
      return;
    }
    if (moveTarget === selectedChannel) {
      append("> DESTINATION = CURRENT CHANNEL. NOTHING TO DO.");
      return;
    }
    const destChannel = channelsForMove.find((c) => c.id === moveTarget);
    const destName = destChannel ? destChannel.name : moveTarget.slice(0, 8);
    setBusy(true);
    append(`> MOVING ${ids.length} TAPE${ids.length === 1 ? "" : "S"} → ${destName.toUpperCase()}...`);
    try {
      const r = await axios.post(
        `${apiBase}/videos/bulk-move`,
        { video_ids: ids, target_channel_id: moveTarget },
        { headers: authHeaders() }
      );
      const { moved = 0, skipped = 0, missing = 0 } = r.data || {};
      append(
        `> DONE. ${moved} MOVED${skipped ? `, ${skipped} ALREADY THERE` : ""}${
          missing ? `, ${missing} NOT FOUND` : ""
        }.`
      );
      cancelMove();
      await onUploaded();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleCreateAndMove = async () => {
    // Two-step pipeline: create the channel, then bulk-move the
    // selection into it. The new channel id is captured from the
    // create response so we don't have to refetch the channels list
    // before the move call. If creation succeeds but the move fails,
    // the empty channel is left in place — owner can populate it
    // manually later. The whole flow exits move-mode on success.
    const name = (newChannelName || "").trim();
    if (!name) {
      append("> NEW CHANNEL NAME REQUIRED.");
      return;
    }
    const ids = Array.from(moveSelection);
    if (!ids.length) {
      append("> NO TAPES SELECTED — NOTHING TO MOVE.");
      return;
    }
    setBusy(true);
    append(`> CREATING CHANNEL "${name.toUpperCase()}"...`);
    try {
      const created = await axios.post(
        `${apiBase}/channels`,
        { name },
        { headers: authHeaders() }
      );
      const newId = created.data && created.data.id;
      if (!newId) {
        append("> CHANNEL CREATED BUT NO ID RETURNED — CHECK MANUALLY.");
        return;
      }
      append(`> CHANNEL OK. MOVING ${ids.length} TAPE${ids.length === 1 ? "" : "S"} INTO IT...`);
      const moveResp = await axios.post(
        `${apiBase}/videos/bulk-move`,
        { video_ids: ids, target_channel_id: newId },
        { headers: authHeaders() }
      );
      const { moved = 0, skipped = 0, missing = 0 } = moveResp.data || {};
      append(
        `> DONE. ${moved} MOVED${skipped ? `, ${skipped} ALREADY THERE` : ""}${
          missing ? `, ${missing} NOT FOUND` : ""
        }.`
      );
      setCreatingChannel(false);
      setNewChannelName("");
      cancelMove();
      await onUploaded();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleReorderVideo = async (videoId, dir) => {
    const idx = channelVideos.findIndex((v) => v.id === videoId);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= channelVideos.length) return;
    const next = [...channelVideos];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setBusy(true);
    append(`> REORDERING PLAYLIST...`);
    try {
      // On sub-channels we can't just POST the sub's filtered ordering
      // as the parent's full order — that would drop every non-show
      // tape. Instead: preserve every parent tape id, but re-locate the
      // moved id one slot up/down in the parent's list (skipping over
      // tapes that belong to other shows). This keeps the sub's UX
      // intuitive without silently discarding siblings.
      let payload;
      if (isSubChannel) {
        // Fetch the parent's current tape order — safer than deriving
        // from the possibly-stale `videosByChannel` prop chain.
        const { data: parentTapes } = await axios.get(
          `${apiBase}/channels/${effectiveChannelId}/videos`
        );
        const parentIds = (parentTapes || []).map((v) => v.id);
        const fromIdx = parentIds.indexOf(videoId);
        if (fromIdx < 0) {
          append("> ERROR: TAPE NOT FOUND ON PARENT CHANNEL");
          setBusy(false);
          return;
        }
        // Walk in the reorder direction until we hit another tape from
        // the same sub-channel (same `show_title`) — that's the tape
        // we swap past. Skip everything else so the sub feels like a
        // standalone list even though the parent order stays coherent.
        const showTitle = selectedCh.show_title;
        const dirStep = dir > 0 ? 1 : -1;
        let targetIdx = fromIdx;
        for (let i = fromIdx + dirStep; i >= 0 && i < parentIds.length; i += dirStep) {
          const sib = (parentTapes || []).find((v) => v.id === parentIds[i]);
          if (sib && (sib.show_title || "").trim() === (showTitle || "").trim()) {
            targetIdx = i;
            break;
          }
        }
        if (targetIdx === fromIdx) {
          setBusy(false);
          return;
        }
        const reordered = [...parentIds];
        const [moved] = reordered.splice(fromIdx, 1);
        reordered.splice(targetIdx, 0, moved);
        payload = { video_ids: reordered };
      } else {
        payload = { video_ids: next.map((v) => v.id) };
      }
      await axios.post(
        `${apiBase}/channels/${effectiveChannelId}/videos/reorder`,
        payload,
        { headers: authHeaders() }
      );
      append("> PLAYLIST UPDATED.");
      await onUploaded();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // One-click natural-alphabetical sort of the entire channel. Useful when
  // a folder upload landed out of order, or watch-folder published episodes
  // as they finished encoding (which isn't always the broadcast order).
  // Same comparator as the upload-side queue sort (numeric-aware) so S1E2
  // < S1E10 etc.
  const handleSortByTitle = async () => {
    if (channelVideos.length < 2) return;
    const sorted = [...channelVideos].sort((a, b) =>
      (a.title || "").localeCompare(b.title || "", undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
    const sameOrder = sorted.every((v, i) => v.id === channelVideos[i].id);
    if (sameOrder) {
      append("> PLAYLIST ALREADY SORTED A→Z.");
      return;
    }
    setBusy(true);
    append(`> SORTING ${channelVideos.length} TAPES BY TITLE (A→Z)...`);
    try {
      // Sub-channels: sort the sub's subset alphabetically then splice
      // the sorted ids back into the parent's full order in-place.
      // Non-show tapes on the parent stay where they were.
      let payload;
      if (isSubChannel) {
        const { data: parentTapes } = await axios.get(
          `${apiBase}/channels/${effectiveChannelId}/videos`
        );
        const subIds = new Set(sorted.map((v) => v.id));
        const parentIds = (parentTapes || []).map((v) => v.id);
        const sortedIter = sorted.map((v) => v.id)[Symbol.iterator]();
        const out = parentIds.map((id) => (subIds.has(id) ? sortedIter.next().value : id));
        payload = { video_ids: out };
      } else {
        payload = { video_ids: sorted.map((v) => v.id) };
      }
      await axios.post(
        `${apiBase}/channels/${effectiveChannelId}/videos/reorder`,
        payload,
        { headers: authHeaders() }
      );
      append("> PLAYLIST SORTED.");
      await onUploaded();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return channelVideos
      .map((v, idx) => ({ v, idx }))
      .filter(({ v }) => {
        if (filterMode === "BLOCKED" && !v.embed_blocked) return false;
        if (filterMode === "MISSING_TMDB" && v.tmdb_id) return false;
        if (filterMode === "UPLOAD" && v.kind !== "upload") return false;
        if (filterMode === "MEGA" && v.kind !== "mega") return false;
        if (filterMode === "YOUTUBE" && v.kind !== "youtube") return false;

        if (!q) return true;
        return [v.title, v.show_title, v.year, v.genre, v.kind]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);
      });
  }, [channelVideos, searchQuery, filterMode]);

  const activeFilterCount = filteredRows.length;

  return (
    <div
      className="h-full min-h-[420px] flex flex-col border-t border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] pt-3 mt-2"
      data-testid="playlist-workspace"
    >
      <div className="flex items-center justify-between gap-3 mb-2 shrink-0">
        <div className="opacity-80">&gt; CURRENT PLAYLIST ({channelVideos.length}):</div>
        <div className="flex items-center gap-1.5">
          {ytCount > 0 && (
            <button
              type="button"
              onClick={handleScanChannel}
              disabled={busy || scanning}
              data-testid="scan-channel"
              className="text-[#5DD3FF] border border-[#5DD3FF]/60 px-2 py-0.5 text-[10px] tracking-widest hover:bg-[#5DD3FF] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={`Re-check embed-status of all ${ytCount} YouTube tape${ytCount === 1 ? "" : "s"} on this channel`}
            >
              {scanning ? "SCANNING…" : `SCAN (${ytCount})`}
            </button>
          )}
          {legacyUnhashedCount > 0 && !bulkMode && !tagShowMode && !moveMode && (
            <button
              type="button"
              onClick={handleAuditHashes}
              disabled={busy || auditing}
              data-testid="audit-hashes"
              className="text-[#5DD3FF] border border-[#5DD3FF]/60 px-2 py-0.5 text-[10px] tracking-widest hover:bg-[#5DD3FF] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={`Backfill content-hash on ${legacyUnhashedCount} legacy tape${legacyUnhashedCount === 1 ? "" : "s"} — enables rename-survival dedup on re-upload.`}
            >
              {auditing ? "AUDITING…" : `AUDIT (${legacyUnhashedCount})`}
            </button>
          )}
          {blockedCount > 0 && (
            <button
              type="button"
              onClick={handlePurgeBlocked}
              disabled={busy}
              data-testid="purge-blocked-videos"
              className="text-[#FFCC00] border border-[#FFCC00]/60 px-2 py-0.5 text-[10px] tracking-widest hover:bg-[#FFCC00] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={`${blockedCount} embed-blocked tape${blockedCount === 1 ? "" : "s"} on this channel`}
            >
              PURGE BLOCKED ({blockedCount})
            </button>
          )}
          {channelVideos.length > 1 && !bulkMode && (
            <button
              type="button"
              onClick={handleSortByTitle}
              disabled={busy}
              data-testid="sort-playlist-az"
              className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-2 py-0.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Reorder all tapes alphabetically (natural, so S1E2 < S1E10)"
            >
              SORT A→Z
            </button>
          )}
          {channelVideos.length > 0 && !bulkMode && !moveMode && !tagShowMode && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              disabled={busy}
              data-testid="playlist-expand"
              title={expanded ? "Switch back to compact tape rows" : "Show extra tape metadata beneath each title"}
              className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-2 py-0.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {expanded ? "COMPACT" : "DETAILS"}
            </button>
          )}
          {channelVideos.length > 0 && !bulkMode && !moveMode && !tagShowMode && (
            <button
              type="button"
              onClick={startBulk}
              disabled={busy}
              data-testid="bulk-edit-titles"
              className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-2 py-0.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Edit every title at once and save in one click"
            >
              BULK EDIT
            </button>
          )}
          {channelVideos.length > 0 && !bulkMode && !moveMode && !tagShowMode && (
            <button
              type="button"
              onClick={() => { setBulkTmdbOpen(true); setExpanded(true); }}
              disabled={busy}
              data-testid="bulk-tmdb-match"
              className="text-[#5DD3FF] border border-[#5DD3FF]/70 px-2 py-0.5 text-[10px] tracking-widest hover:bg-[#5DD3FF] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Search the current playlist against TMDb in bulk, review proposed matches, then apply selected metadata"
            >
              🎬 BULK TMDB
            </button>
          )}
          {channelVideos.length > 0 && !bulkMode && !moveMode && !tagShowMode && (
            <button
              type="button"
              onClick={startTagShow}
              disabled={busy}
              data-testid="start-tag-show"
              className="text-[#FFAA33] border border-[#FFAA33]/70 px-2 py-0.5 text-[10px] tracking-widest hover:bg-[#FFAA33] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Multi-select tapes and stamp a show_title on all of them — spawns a Sky-Q sub-channel once ≥ 2 distinct shows sit on this channel"
            >
              🎬 TAG SHOW
            </button>
          )}
          {channelVideos.length > 0 && !bulkMode && !moveMode && !tagShowMode && channelsForMove.length > 1 && (
            <button
              type="button"
              onClick={startMove}
              disabled={busy}
              data-testid="start-move-tapes"
              className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-2 py-0.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Multi-select tapes and move them to another channel"
            >
              MOVE TAPES
            </button>
          )}
          {bulkMode && (
            <>
              <button
                type="button"
                onClick={saveBulk}
                disabled={busy}
                data-testid="bulk-save-titles"
                className="bg-[var(--phosphor)] text-black px-2 py-0.5 text-[10px] tracking-widest font-bold hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                SAVE ALL
              </button>
              <button
                type="button"
                onClick={cancelBulk}
                disabled={busy}
                data-testid="bulk-cancel-titles"
                className="text-neutral-400 border border-neutral-600 px-2 py-0.5 text-[10px] tracking-widest hover:border-neutral-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                CANCEL
              </button>
            </>
          )}
          {moveMode && (
            <>
              <button
                type="button"
                onClick={selectAllForMove}
                disabled={busy || moveSelection.size === channelVideos.length}
                data-testid="move-select-all"
                className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-2 py-0.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                SELECT ALL
              </button>
              <button
                type="button"
                onClick={cancelMove}
                disabled={busy}
                data-testid="move-cancel"
                className="text-neutral-400 border border-neutral-600 px-2 py-0.5 text-[10px] tracking-widest hover:border-neutral-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                CANCEL
              </button>
            </>
          )}
          {channelVideos.length > 0 && !bulkMode && !moveMode && !tagShowMode && (
            <button
              type="button"
              onClick={handleDeleteAll}
              disabled={busy}
              data-testid="delete-all-videos"
              title="Delete every tape in the current playlist — confirmation is required"
              className="text-[#FF3B30] border border-[#FF3B30]/60 px-2 py-0.5 text-[10px] tracking-widest hover:bg-[#FF3B30] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              DEL ALL
            </button>
          )}
        </div>
      </div>

      <div
        className="shrink-0 flex flex-col lg:flex-row lg:items-center gap-2 mb-2 p-2 border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] bg-black/25"
        data-testid="playlist-browser-toolbar"
      >
        <div className="flex-1 min-w-0">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="SEARCH TAPES · title, show, year, genre..."
            data-testid="playlist-search"
            title="Filter the current playlist instantly without changing tape order"
            className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] text-[var(--phosphor)] px-2.5 py-1.5 text-[10px] tracking-wide outline-none focus:border-[var(--phosphor)] placeholder:text-[color-mix(in_srgb,var(--phosphor)_32%,transparent)]"
          />
        </div>

        <div className="flex items-center gap-1.5 flex-wrap shrink-0">
          <span className="text-[9px] tracking-[0.18em] opacity-45">FILTER</span>
          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
            data-testid="playlist-filter"
            title="Limit visible rows. Filtering never deletes or reorders tapes."
            className="bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] text-[var(--phosphor)] px-2 py-1 text-[10px] outline-none focus:border-[var(--phosphor)]"
          >
            <option value="ALL">ALL TAPES</option>
            <option value="BLOCKED">BLOCKED</option>
            <option value="MISSING_TMDB">MISSING TMDB</option>
            <option value="UPLOAD">UPLOADS</option>
            <option value="MEGA">MEGA</option>
            <option value="YOUTUBE">YOUTUBE</option>
          </select>

          <span
            className="text-[9px] tracking-[0.16em] opacity-55 tabular-nums px-2"
            data-testid="playlist-visible-count"
            title="Rows visible after the current search and filter"
          >
            {activeFilterCount} / {channelVideos.length} VISIBLE
          </span>

          {(searchQuery || filterMode !== "ALL") && (
            <button
              type="button"
              onClick={() => { setSearchQuery(""); setFilterMode("ALL"); }}
              data-testid="playlist-clear-filter"
              title="Clear playlist search and filters"
              className="text-[9px] tracking-widest border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] px-2 py-1 opacity-70 hover:opacity-100 hover:border-[var(--phosphor)]"
            >
              CLEAR
            </button>
          )}
        </div>
      </div>

      {/* Tag-show floating action bar — show-title input + apply. Shown
          only in tagShowMode, mutex with moveMode. datalist surfaces
          existing show titles on this channel so operators tagging a
          2nd/3rd batch pick from the list instead of re-typing (and
          avoid split-by-typo variants that would spawn extra
          sub-channels). Empty input + APPLY explicitly UNTAGs. */}
      {tagShowMode && (
        <div
          className="shrink-0 flex flex-wrap items-center gap-2 px-2 py-1.5 border border-[#FFAA33]/40 bg-[color-mix(in_srgb,#FFAA33_5%,black)] mb-1"
          data-testid="tag-show-action-bar"
        >
          <span className="text-[10px] tracking-[0.2em] text-[#FFAA33] opacity-90 shrink-0">
            {moveSelection.size} OF {channelVideos.length} SELECTED · TAG AS →
          </span>
          <input
            list="tag-show-suggestions"
            value={tagShowDraft}
            onChange={(e) => setTagShowDraft(e.target.value)}
            disabled={busy}
            placeholder="e.g. Sonic Underground   (blank = untag)"
            autoFocus
            maxLength={120}
            onKeyDown={(e) => {
              if (e.key === "Enter" && moveSelection.size > 0) handleBulkTagShow();
              else if (e.key === "Escape") cancelTagShow();
            }}
            data-testid="tag-show-input"
            className="flex-1 min-w-[180px] bg-black border border-[#FFAA33]/70 text-[var(--phosphor)] px-2 py-1 text-[11px] outline-none font-mono placeholder:text-[color-mix(in_srgb,#FFAA33_40%,transparent)]"
          />
          <datalist id="tag-show-suggestions">
            {existingShowTitles.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <button
            type="button"
            onClick={handleBulkTagShow}
            disabled={busy || !moveSelection.size}
            data-testid="tag-show-apply"
            title={tagShowDraft.trim() ? `Stamp show_title="${tagShowDraft.trim()}" on all selected tapes` : "Blank input clears show_title (UNTAG)"}
            className="bg-[#FFAA33] text-black px-3 py-1 text-[10px] tracking-widest font-bold hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {tagShowDraft.trim() ? "APPLY" : "UNTAG"}
          </button>
          {existingShowTitles.length > 0 && (
            <span className="text-[9px] opacity-60 tracking-widest shrink-0 basis-full">
              KNOWN ON THIS CH: {existingShowTitles.slice(0, 6).join(" · ")}
              {existingShowTitles.length > 6 && ` +${existingShowTitles.length - 6}`}
            </span>
          )}
        </div>
      )}
      {/* Move-mode floating action bar — channel picker + move count */}
      {moveMode && (
        <div
          className="shrink-0 flex flex-col gap-1.5 px-2 py-1.5 border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] bg-[color-mix(in_srgb,var(--phosphor)_4%,black)] mb-1"
          data-testid="move-action-bar"
        >
          {!creatingChannel ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] tracking-[0.2em] text-[var(--phosphor)] opacity-80">
                {moveSelection.size} OF {channelVideos.length} SELECTED →
              </span>
              <select
                value={moveTarget}
                onChange={(e) => setMoveTarget(e.target.value)}
                disabled={busy}
                data-testid="move-target-channel"
                className="flex-1 min-w-[140px] bg-black border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] text-[var(--phosphor)] text-[11px] tracking-wide px-2 py-1 outline-none font-mono"
              >
                <option value="">— DESTINATION —</option>
                {channelsForMove
                  .filter((c) => c.id !== selectedChannel)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {(c.number != null ? `CH ${String(c.number).padStart(2, "0")} · ` : "") + (c.name || "(unnamed)")}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                onClick={handleBulkMove}
                disabled={busy || !moveSelection.size || !moveTarget}
                data-testid="move-confirm"
                className="bg-[var(--phosphor)] text-black px-3 py-1 text-[10px] tracking-widest font-bold hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                MOVE
              </button>
              <button
                type="button"
                onClick={() => { setCreatingChannel(true); setMoveTarget(""); }}
                disabled={busy || !moveSelection.size}
                data-testid="move-new-channel"
                title="Create a brand-new channel and move the selected tapes into it"
                className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-2 py-1 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                + NEW CHANNEL
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] tracking-[0.2em] text-[var(--phosphor)] opacity-80 shrink-0">
                NEW CH NAME →
              </span>
              <input
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value.slice(0, 60))}
                disabled={busy}
                placeholder="e.g. SCI-FI"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateAndMove();
                  else if (e.key === "Escape") { setCreatingChannel(false); setNewChannelName(""); }
                }}
                data-testid="move-new-channel-name"
                className="flex-1 min-w-[140px] bg-black border border-[var(--phosphor)] text-[var(--phosphor)] px-2 py-1 text-[11px] outline-none font-mono placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
              />
              <button
                type="button"
                onClick={handleCreateAndMove}
                disabled={busy || !newChannelName.trim() || !moveSelection.size}
                data-testid="move-new-channel-confirm"
                className="bg-[var(--phosphor)] text-black px-3 py-1 text-[10px] tracking-widest font-bold hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                CREATE + MOVE
              </button>
              <button
                type="button"
                onClick={() => { setCreatingChannel(false); setNewChannelName(""); }}
                disabled={busy}
                data-testid="move-new-channel-cancel"
                className="text-neutral-400 border border-neutral-600 px-2 py-1 text-[10px] tracking-widest hover:border-neutral-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                BACK
              </button>
            </div>
          )}
        </div>
      )}
      <div
        className="flex-1 min-h-0 flex flex-col gap-1 overflow-y-auto scrollbar-crt pr-1"
        data-testid="playlist-scroll"
      >
        {channelVideos.length === 0 && (
          <div className="opacity-60 p-3">[ EMPTY — INSERT TAPE TO BEGIN ]</div>
        )}
        {channelVideos.length > 0 && filteredRows.length === 0 && (
          <div className="opacity-60 p-3 border border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)]">
            [ NO TAPES MATCH THE CURRENT SEARCH / FILTER ]
          </div>
        )}
        {filteredRows.map(({ v, idx }) => (
          <div key={v.id} className="border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-1.5" data-testid={`playlist-row-${idx}`}>
            <div className="flex items-center justify-between gap-2">
              {moveMode || tagShowMode ? (
                <label
                  className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                  data-testid={`${tagShowMode ? "tag-show" : "move"}-row-${idx}`}
                >
                  <input
                    type="checkbox"
                    checked={moveSelection.has(v.id)}
                    onChange={() => toggleMoveSelect(v.id)}
                    disabled={busy}
                    data-testid={`${tagShowMode ? "tag-show" : "move"}-checkbox-${idx}`}
                    className={`${tagShowMode ? "accent-[#FFAA33]" : "accent-[var(--phosphor)]"} w-4 h-4 shrink-0`}
                  />
                  <span className="opacity-60 shrink-0 text-[11px] tabular-nums">{String(idx + 1).padStart(2, "0")}.</span>
                  <span className="truncate text-[12px]">{v.title || "(untitled)"}</span>
                  {tagShowMode && v.show_title && (
                    <span
                      className="ml-1 shrink-0 text-[9px] px-1 border border-[#FFAA33]/60 text-[#FFAA33] tracking-widest"
                      title={`Current show_title: ${v.show_title}`}
                    >
                      {v.show_title.length > 22 ? `${v.show_title.slice(0, 21)}…` : v.show_title}
                    </span>
                  )}
                </label>
              ) : bulkMode ? (
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <span className="opacity-60 shrink-0 text-[11px] tabular-nums">{String(idx + 1).padStart(2, "0")}.</span>
                  <input
                    value={draftTitles[v.id] ?? v.title ?? ""}
                    onChange={(e) => setDraftTitles((m) => ({ ...m, [v.id]: e.target.value }))}
                    disabled={busy}
                    data-testid={`bulk-title-input-${idx}`}
                    className="flex-1 bg-black border border-[var(--phosphor)] text-[var(--phosphor)] px-1.5 py-1 text-[11px] outline-none disabled:opacity-50"
                  />
                </div>
              ) : (
                <div className="min-w-0 flex-1" data-testid={`playlist-item-${idx}`}>
                  <div className="truncate">
                    <span className="opacity-55 tabular-nums mr-2">{String(idx + 1).padStart(2, "0")}.</span>
                    <span>{v.title}</span>
                    {v.year && <span className="opacity-60"> · {v.year}</span>}
                    {v.genre && <span className="opacity-60"> · {v.genre}</span>}
                  </div>
                  {expanded && (
                    <div className="mt-0.5 ml-7 flex items-center gap-2 flex-wrap text-[9px] tracking-wide opacity-50">
                      <span>{(v.kind || "tape").toUpperCase()}</span>
                      {v.show_title && <span>SHOW · {v.show_title}</span>}
                      {v.season_number != null && <span>S{String(v.season_number).padStart(2, "0")}</span>}
                      {v.episode_number != null && <span>E{String(v.episode_number).padStart(2, "0")}</span>}
                      <span>{v.tmdb_id ? `TMDB · ${v.tmdb_id}` : "TMDB · MISSING"}</span>
                    </div>
                  )}
                  {v.embed_blocked && (
                    <span
                      className={`ml-2 text-[9px] text-black px-1 font-bold tracking-wider ${
                        v.embed_blocked_source === "runtime-onerror"
                          ? "bg-[#FF6B35]"
                          : "bg-[#FFCC00]"
                      }`}
                      data-testid={`playlist-blocked-${idx}`}
                      title={
                        v.embed_blocked_source === "runtime-onerror"
                          ? "Caught at runtime — a viewer's iframe failed (Data API said OK)"
                          : "Flagged by nightly YouTube Data API scan"
                      }
                    >
                      ⚠ {v.embed_blocked_source === "runtime-onerror" ? "RUNTIME" : "BLOCKED"}
                    </span>
                  )}
                  {(() => {
                    const badge = lufsBadge(v);
                    if (!badge) return null;
                    const colour =
                      badge.tone === "ok"
                        ? "border-[var(--phosphor)] text-[var(--phosphor)]"
                        : badge.tone === "warn"
                          ? "border-[#FF6B35] text-[#FF6B35]"
                          : "border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] text-[color-mix(in_srgb,var(--phosphor)_55%,transparent)]";
                    return (
                      <span
                        className={`ml-2 text-[9px] px-1 tracking-wider tabular-nums border ${colour}`}
                        data-testid={`playlist-lufs-${idx}`}
                        title={badge.title}
                      >
                        {badge.text}
                      </span>
                    );
                  })()}
                </div>
              )}
              {!bulkMode && !moveMode && !tagShowMode && (
                <div className="flex gap-1 shrink-0">
                  {v.kind === "upload" && (
                    <button
                      onClick={() => reanalyseVideo(v.id)}
                      disabled={busy || !!reanalysing[v.id]}
                      data-testid={`reanalyse-video-${idx}`}
                      title="Re-run loudness analysis for this tape"
                      className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-1.5 hover:bg-[var(--phosphor)] hover:text-black transition-colors text-[10px] disabled:opacity-40"
                    >
                      {reanalysing[v.id] ? "…" : "↻"}
                    </button>
                  )}
                  <button
                    onClick={() => handleReorderVideo(v.id, -1)}
                    disabled={busy || idx === 0}
                    data-testid={`video-up-${idx}`}
                      title="Move this tape one position earlier in the playlist"
                    className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-1.5 hover:bg-[var(--phosphor)] hover:text-black transition-colors text-[10px] disabled:opacity-30"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => handleReorderVideo(v.id, 1)}
                    disabled={busy || idx === channelVideos.length - 1}
                    data-testid={`video-down-${idx}`}
                      title="Move this tape one position later in the playlist"
                    className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-1.5 hover:bg-[var(--phosphor)] hover:text-black transition-colors text-[10px] disabled:opacity-30"
                  >
                    ▼
                  </button>
                  <button
                    onClick={() => setTmdbTarget(v)}
                    disabled={busy}
                    data-testid={`tmdb-lookup-${idx}`}
                    title="Pull poster + synopsis from TheMovieDB"
                    className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-1.5 hover:bg-[var(--phosphor)] hover:text-black transition-colors text-[10px] disabled:opacity-40"
                  >
                    🎬
                  </button>
                  <button
                    onClick={async () => {
                      // Copy the tape's UUID to the clipboard so the
                      // operator can paste it into Tribute audio,
                      // manual DB overrides, deep-link URLs, etc.
                      // without hunting through the DB. Falls back to
                      // the classic execCommand path for older browsers
                      // (still hit occasionally in the CRT / smart-TV
                      // audience). Toast confirmation because the
                      // clipboard action is silent.
                      const idStr = String(v.id || "");
                      try {
                        if (navigator.clipboard && window.isSecureContext) {
                          await navigator.clipboard.writeText(idStr);
                        } else {
                          const ta = document.createElement("textarea");
                          ta.value = idStr;
                          ta.style.position = "fixed";
                          ta.style.opacity = "0";
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand("copy");
                          document.body.removeChild(ta);
                        }
                        toast.success("📋 TAPE ID COPIED", {
                          description: idStr,
                          duration: 3500,
                        });
                        append(`> COPIED TAPE ID · ${idStr}`);
                      } catch (e) {
                        toast.error("COPY FAILED", { description: e.message });
                        append(`> COPY FAILED · ${e.message}`);
                      }
                    }}
                    disabled={busy}
                    data-testid={`copy-video-id-${idx}`}
                    title={`Copy tape UUID (${v.id}) to clipboard — paste it into Tribute audio, deep-links, or manual overrides.`}
                    className="text-neutral-300 border border-neutral-500/60 px-1.5 hover:bg-neutral-300 hover:text-black transition-colors text-[10px] disabled:opacity-40"
                  >
                    📋
                  </button>
                  <button
                    onClick={async () => {
                      // Build a shareable deep-link to the tape:
                      // `/ch/<number>/t/<tape-id>`. Uses the parent
                      // channel's number for sub-channels (their subs
                      // live under the parent route). Falls back to
                      // the tape's own channel_id when the picker
                      // hasn't hydrated yet. Native share sheet on
                      // mobile / Win11 via Web Share API, clipboard
                      // + toast on desktop — same helper used by the
                      // remote's SHARE button.
                      const parent = isSubChannel
                        ? (channels || []).find((c) => c.id === selectedCh.parent_id)
                        : selectedCh;
                      const chNum = parent?.number;
                      if (!chNum) {
                        toast.error("SHARE FAILED", { description: "Channel number unavailable" });
                        return;
                      }
                      const url = `${window.location.origin}/ch/${chNum}/t/${v.id}`;
                      const title = (v.title || "").trim() || "TAPE";
                      const label = `📎 TAPE LINK COPIED · CH ${String(chNum).padStart(2, "0")}`;
                      await shareUrl(url, label, title);
                      append(`> COPIED TAPE LINK · ${url}`);
                    }}
                    disabled={busy}
                    data-testid={`copy-video-link-${idx}`}
                    title="Copy shareable deep-link to this tape (opens native share sheet on mobile, clipboard on desktop)."
                    className="text-neutral-300 border border-neutral-500/60 px-1.5 hover:bg-neutral-300 hover:text-black transition-colors text-[10px] disabled:opacity-40"
                  >
                    📎
                  </button>
                  <button
                    onClick={() => setEditingVideo(editingVideo === v.id ? null : v.id)}
                    disabled={busy}
                    data-testid={`edit-video-${idx}`}
                      title="Edit this tape title, metadata and playback settings"
                    className="text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] px-2 hover:bg-[var(--phosphor)] hover:text-black transition-colors text-[10px]"
                  >
                    EDIT
                  </button>
                  <button
                    onClick={() => handleDeleteVideo(v.id)}
                    disabled={busy}
                    data-testid={`delete-video-${idx}`}
                      title="Delete this tape from the current channel"
                    className="text-[#FF3B30] border border-[#FF3B30]/60 px-2 hover:bg-[#FF3B30] hover:text-black transition-colors text-[10px]"
                  >
                    DEL
                  </button>
                </div>
              )}
            </div>
            {!bulkMode && editingVideo === v.id && (
              <VideoEditor
                video={v}
                apiBase={apiBase}
                authHeaders={authHeaders}
                onSaved={async () => {
                  await onUploaded(v.channel_id);
                  setEditingVideo(null);
                }}
                onCancel={() => setEditingVideo(null)}
                append={append}
              />
            )}
          </div>
        ))}
      </div>
      {/* TMDb picker — mounted once at the panel level; the currently-
          selected tape drives its target. Clears on close. */}
      <BulkTmdbMatchModal
        open={bulkTmdbOpen}
        videos={channelVideos}
        apiBase={apiBase}
        authHeaders={authHeaders}
        append={append}
        onClose={() => setBulkTmdbOpen(false)}
        onApplied={async () => { await onUploaded(effectiveChannelId); }}
      />
      <TmdbLookupModal
        open={!!tmdbTarget}
        initialQuery={tmdbTarget?.title || ""}
        initialYear={tmdbTarget?.year || null}
        mode="video"
        targetId={tmdbTarget?.id}
        apiBase={apiBase}
        authHeaders={authHeaders}
        onClose={() => setTmdbTarget(null)}
        onApplied={async () => {
          if (tmdbTarget) await onUploaded(tmdbTarget.channel_id);
        }}
      />
    </div>
  );
}
